<script setup lang="ts">
const prefix = 'components.legal.privacyPolicy';
</script>

<template>
  <div>
    <div class="w-full bg-secondary px-4 pb-5 pt-10 md:px-6 lg:px-8">
      <UiCustomContainer>
        <NuxtLinkLocale
          to="index"
          class="mb-8 inline-flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <SvgoCommonArrowLeft
            filled
            :font-controlled="false"
            class="mt-0.5 h-5 w-5"
          />
          <span class="font-semibold">{{ $t(`${prefix}.backToHome`) }}</span>
        </NuxtLinkLocale>

        <h1 class="mb-10 text-pretty text-center text-3xl font-bold">
          {{ $t('pages.privacyPolicy.seo.title') }}
        </h1>
      </UiCustomContainer>
    </div>

    <div class="flex w-full justify-center px-4 py-10 md:px-6 lg:px-8">
      <div class="max-w-container">
        <div class="mb-8 flex items-center gap-2 text-sm text-gray-600">
          <SvgoLegalClock filled />
          <span>{{ $t(`${prefix}.updatedAt`) }}</span>
        </div>

        <div class="space-y-4 text-secondary-foreground">
          <!-- Section 1: Introduction -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">1. {{ $t(`${prefix}.s1.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s1.p1`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">
            {{ $t(`${prefix}.s1.p2`) }}
            <NuxtLink
              to="mailto:dpo@optionway.com"
              class="text-foreground underline"
              >dpo@optionway.com</NuxtLink
            >.
          </p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s1.p3`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s1.p4`) }}</p>

          <!-- Section 2: Data collected -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">2. {{ $t(`${prefix}.s2.title`) }}</h2>
          <h3 class="text-lg font-medium md:text-xl">2.1. {{ $t(`${prefix}.s2.s21.title`) }}</h3>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s2.s21.p1`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s2.s21.p2`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s2.s21.p3`) }}</p>
          <h3 class="text-lg font-medium md:text-xl">2.2. {{ $t(`${prefix}.s2.s22.title`) }}</h3>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s2.s22.p1`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s2.s22.p2`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s2.s22.p3`) }}</p>

          <!-- Section 3: Processing and use of data -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">3. {{ $t(`${prefix}.s3.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s3.intro`) }}</p>
          <div class="space-y-2 pl-4">
            <p class="text-base font-semibold md:text-lg">a) {{ $t(`${prefix}.s3.a.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s3.a.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">b) {{ $t(`${prefix}.s3.b.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s3.b.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">c) {{ $t(`${prefix}.s3.c.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s3.c.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">d) {{ $t(`${prefix}.s3.d.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s3.d.desc`) }}</p>
          </div>

          <!-- Section 4: Cookie management -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">4. {{ $t(`${prefix}.s4.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s4.p1`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s4.cookieList`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s4.p2`) }}</p>

          <!-- Section 5: Data sharing -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">5. {{ $t(`${prefix}.s5.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s5.p1`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s5.p2`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s5.p3`) }}</p>

          <!-- Section 6: Payment information -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">6. {{ $t(`${prefix}.s6.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s6.p1`) }}</p>

          <!-- Section 7: Data storage and security -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">7. {{ $t(`${prefix}.s7.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s7.p1`) }}</p>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s7.p2`) }}</p>

          <!-- Section 8: Your rights regarding your personal data -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">8. {{ $t(`${prefix}.s8.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.intro`) }}</p>
          <div class="space-y-2 pl-4">
            <p class="text-base font-semibold md:text-lg">a) {{ $t(`${prefix}.s8.a.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.a.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">b) {{ $t(`${prefix}.s8.b.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.b.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">c) {{ $t(`${prefix}.s8.c.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.c.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">d) {{ $t(`${prefix}.s8.d.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.d.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">e) {{ $t(`${prefix}.s8.e.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.e.desc`) }}</p>
            <p class="text-base font-semibold md:text-lg">f) {{ $t(`${prefix}.s8.f.title`) }}</p>
            <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s8.f.desc`) }}</p>
          </div>
          <p class="text-justify text-base font-normal md:text-lg">
            {{ $t(`${prefix}.s8.pContact`) }}
            <NuxtLink
              to="mailto:dpo@optionway.com"
              class="text-foreground underline"
              >dpo@optionway.com</NuxtLink
            >.
          </p>

          <!-- Section 9: Links to other websites and social networks -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">9. {{ $t(`${prefix}.s9.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s9.p1`) }}</p>

          <!-- Section 10: Amendment of our privacy policy -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">10. {{ $t(`${prefix}.s10.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">{{ $t(`${prefix}.s10.p1`) }}</p>

          <!-- Section 11: Contact -->
          <h2 class="pt-4 text-xl font-medium text-foreground md:text-2xl">11. {{ $t(`${prefix}.s11.title`) }}</h2>
          <p class="text-justify text-base font-normal md:text-lg">
            {{ $t(`${prefix}.s11.p1Before`) }}
            <NuxtLink
              to="mailto:dpo@optionway.com"
              class="text-foreground underline"
              >dpo@optionway.com</NuxtLink
            >
            {{ $t(`${prefix}.s11.p1After`) }}
          </p>

          <p>
            <span class="text-base font-normal md:text-lg">{{ $t(`${prefix}.s11.address1`) }}</span> <br />
            <span class="text-base font-normal md:text-lg">{{ $t(`${prefix}.s11.address2`) }}</span> <br />
            <span class="text-base font-normal md:text-lg">{{ $t(`${prefix}.s11.address3`) }}</span> <br />
            <span class="text-base font-normal md:text-lg">{{ $t(`${prefix}.s11.address4`) }}</span> <br />
            <span class="text-base font-normal md:text-lg">{{ $t(`${prefix}.s11.address5`) }}</span> <br />
            <span class="text-base font-normal md:text-lg">{{ $t(`${prefix}.s11.address6`) }}</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
